import "@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css";
import "./styles.css"; // this is added for arrow icon. Because of CSP, the package can't load arrow with data svg image.

import {
  Button,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
} from "@mui/material";
import { Calendar, Day } from "@hassanmojab/react-modern-calendar-datepicker";

import { IDatePickerDate } from "./types";
import clsx from "clsx";
import farvardin from "./farvardin";
import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatPersianDate } from "utils/index";
import { Icon } from "components/Icon";

interface IDatePicker {
  callback: (response: any) => void;
  label: string;
  className?: string;
  inputClassName?: string;
  defaultValueDate?: string;
  showDefaultDate?: boolean;
  defaultError?: boolean;
  disabled?: boolean;
  helperText?: string;
  name?: string;
  maximumDate?: string;
  minimumDate?: string;
}

export function DatePicker({
  callback,
  label,
  defaultValueDate,
  showDefaultDate = true,
  className,
  inputClassName,
  defaultError,
  disabled,
  helperText,
  name,
  maximumDate,
  minimumDate,
  ...rest
}: IDatePicker) {
  const [isNew, setIsSetNew] = useState(false);
  const [error, setError] = useState(defaultError);

  useEffect(() => {
    setError(defaultError);
  }, [defaultError]);

  const nowDateAndTimeForPickerValue = useMemo(() => {
    const currentDate = new Date();
    const currentYearInPersian = parseInt(
      new Intl.DateTimeFormat("en-US-u-ca-persian", {
        year: "numeric",
      })
        .format(currentDate)
        .substring(0, 4)
    );
    const currentMonth = parseInt(
      new Intl.DateTimeFormat("en-US-u-ca-persian", {
        month: "numeric",
      }).format(currentDate)
    );
    const currentDay = parseInt(
      new Intl.DateTimeFormat("en-US-u-ca-persian", {
        day: "numeric",
      }).format(currentDate)
    );

    return {
      year: currentYearInPersian,
      month: currentMonth,
      day: currentDay,
    };
  }, []);
  //

  const [selectedDay, setSelectedDay] = useState<any>(
    nowDateAndTimeForPickerValue
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    //TODO: need refactor
    if (showDefaultDate) {
      callback(selectedDay);
    } else {
      setSelectedDay(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prepareDate = useCallback((date?: string) => {
    setIsSetNew(true);
    const defaultDay = date ? new Date(date) : new Date();
    try {
      setSelectedDay(formatPersianDate(defaultDay));
    } catch {}
    setIsSetNewTimout();
  }, []);

  const setIsSetNewTimout = () => {
    setTimeout(() => {
      setIsSetNew(false);
    }, 0);
  };

  useEffect(() => {
    if (isEmpty(defaultValueDate)) {
      setSelectedDay(null);
      return;
    }
    prepareDate(defaultValueDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValueDate]);

  const prepareMaximumDate = useCallback((date?: string) => {
    const defaultDay = date ? new Date(date) : new Date();
    if (date) {
      return formatPersianDate(defaultDay);
    } else {
      return null;
    }
  }, []);

  const open = Boolean(anchorEl);
  const id: string | undefined = open ? "simple-popover" : undefined;

  const [dateInput, setDateInput] = useState("");
  useEffect(() => {
    setDateInput(
      !selectedDay?.year
        ? ""
        : `${selectedDay?.year}/${selectedDay?.month}/${selectedDay?.day}`
    );
  }, [selectedDay]);

  const dateRegExp = new RegExp(
    /^\d{4}\/([1-9]|1[0-2])\/([1-9]|[12][0-9]|3[01])$/
  );

  const checkInputBetweenMinMax = useCallback(
    (input: string) => {
      if (!input) return true;

      const inputDateArray = input.split("/");
      const inputDate = {
        year: parseInt(inputDateArray[0]),
        month: parseInt(inputDateArray[1]),
        day: parseInt(inputDateArray[2]),
      };

      let minJalaliDate;
      let maxJalaliDate;
      if (minimumDate) {
        minJalaliDate = prepareMaximumDate(minimumDate);
      }
      if (maximumDate) {
        maxJalaliDate = prepareMaximumDate(maximumDate);
      }

      const isAfterMin =
        !minJalaliDate ||
        inputDate.year > minJalaliDate.year ||
        (inputDate.year === minJalaliDate.year &&
          (inputDate.month > minJalaliDate.month ||
            (inputDate.month === minJalaliDate.month &&
              inputDate.day >= minJalaliDate.day)));

      const isBeforeMax =
        !maxJalaliDate ||
        inputDate.year < maxJalaliDate.year ||
        (inputDate.year === maxJalaliDate.year &&
          (inputDate.month < maxJalaliDate.month ||
            (inputDate.month === maxJalaliDate.month &&
              inputDate.day <= maxJalaliDate.day)));

      return isAfterMin && isBeforeMax;
    },
    [maximumDate, minimumDate]
  );

  const onDateInputChange = (e: any) => {
    const dateInputValue = e.target.value;

    setDateInput(dateInputValue);

    if (dateRegExp.test(dateInputValue)) {
      const isValidBaseOnMinMax = checkInputBetweenMinMax(dateInputValue);

      if (isValidBaseOnMinMax) {
        const dateArrayFromString = dateInputValue.split("/");
        const date = {
          year: parseInt(dateArrayFromString[0]),
          month: parseInt(dateArrayFromString[1]),
          day: parseInt(dateArrayFromString[2]),
        };

        callback(date);
        setSelectedDay(date);
        setError(false);
        return;
      } else {
        setError(true);
        callback("");
        setSelectedDay(null);
      }
    }

    setError(true);
  };

  return (
    <div
      className={clsx(
        "flex align-center items-start flex-col",
        disabled && "opacity-55",
        className
      )}
    >
      <TextField
        label={label}
        name={name}
        className={clsx("!p-0", inputClassName, "w-full")}
        value={dateInput}
        disabled={disabled}
        onChange={onDateInputChange}
        InputProps={{
          classes: { input: "p-[8px] text-center" },
          size: "small", // const dateArrayFromString = dateInputValue.split("/");
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                disabled={disabled}
                className="m-0 p-0"
                onClick={(event: any) => setAnchorEl(event.currentTarget)}
              >
                <Icon
                  icon="MdCalendarMonth"
                  className="text-lg hover:text-mofidGray-150"
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          size: "small",
        }}
        error={error}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        sx={[
          {
            "& .MuiPopover-paper": {
              boxShadow:
                "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
            },
          },
        ]}
      >
        <div className="flex flex-col justify-center">
          <Calendar
            {...rest}
            locale="fa"
            key={"calendar" + isNew}
            value={selectedDay}
            maximumDate={prepareMaximumDate(maximumDate) as Day}
            minimumDate={prepareMaximumDate(minimumDate) as Day}
            onChange={(e: any) => {
              setError(false);
              setSelectedDay(e);
              callback(e);
              setAnchorEl(null);
            }}
            shouldHighlightWeekends={true}
            colorPrimary="#14304D"
            colorPrimaryLight="#14304D90"
          />
          <div className="bg-mofidGray-white w-full z-10 flex justify-between items-center px-2 pb-2">
            <div>
              <Button
                className="!font-[inherit]"
                onClick={() => {
                  setIsSetNew(true);
                  if (selectedDay)
                    setSelectedDay((date: any) => {
                      date.year = date.year - 1;
                      return { ...date };
                    });
                  else {
                    const date = new Date();
                    date.setFullYear(date.getFullYear() - 1);
                    setSelectedDay(formatPersianDate(date));
                  }
                  setIsSetNewTimout();
                }}
              >
                سال قبل
              </Button>
              <Button
                className="!font-[inherit]"
                onClick={() => {
                  setIsSetNew(true);
                  if (selectedDay)
                    setSelectedDay((date: any) => {
                      date.year = date.year + 1;
                      return { ...date };
                    });
                  else {
                    const date = new Date();
                    date.setFullYear(date.getFullYear() + 1);
                    setSelectedDay(formatPersianDate(date));
                  }
                  setIsSetNewTimout();
                }}
              >
                سال بعد
              </Button>
            </div>
            <Button
              className="!font-[inherit]"
              onClick={prepareDate.bind(null, undefined)}
            >
              برو به امروز
            </Button>
          </div>
        </div>
      </Popover>
      {helperText && (
        <span className="mr-3 mt-1 text-mofidError-80 text-[0.643rem]">
          {helperText}
        </span>
      )}
    </div>
  );
}

export const useDateForQueryParams = (): [
  (
    date: IDatePickerDate,
    endOfDayTime?: boolean,
    dateOnly?: boolean
  ) => string | undefined,
] => {
  const convertDateForQueryParam = (
    datePickerDate?: IDatePickerDate,
    endOfDayTime = false,
    dateOnly = false
  ) => {
    if (!datePickerDate) return;
    const convertedDate = farvardin
      .solarToGregorian(
        datePickerDate.year,
        datePickerDate.month,
        datePickerDate.day,
        "string"
      )
      .toString()
      .replaceAll(",", "-");
    if (dateOnly) {
      return `${convertedDate}`; //Example: 2023-02-01
    }
    return `${convertedDate}T${endOfDayTime ? "23:59:59" : "00:00:00"}`; //Example: 2023-02-01T12:13:11
  };
  return [convertDateForQueryParam];
};
